import {
  Button,
  CustomFilter,
  DatePicker,
  Form,
  Modal,
  Paging,
  Select,
  Table,
  useBreadcrumbRoutes,
  useSearchParams,
  Wrapper,
  message,
} from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DeviationAlarmRecordItem,
  getTableListColumns,
  parseAlarmObjectValue,
  useDeviationRecordOptions,
} from './service';
import { Space } from 'antd';
import DetailModal from './components/DetailModal';
import {
  apiV2DeviationRateAlarmRecordDeletePost,
  apiV2DeviationRateAlarmRecordManualTriggerPost,
  apiV2DeviationRateAlarmRecordPagePost,
  V2DeviationRateAlarmRecordPagePostRequest,
} from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';

// 表单值类型
type FormValues = Partial<{
  alarmObjects: string[]; // 报警对象，格式: "energyMediumTopologyId-topologyNodeId"
  energyMediumId: number; // 能源介质，单选
  deviationRateAlarmRuleIds: number[];
  alarmLevels: number[];
  alarmTimeRange: [dayjs.Dayjs, dayjs.Dayjs];
  statTimeRange: [dayjs.Dayjs, dayjs.Dayjs];
}>;

type SearchParams = FormValues & {
  pageOffset: number;
  pageSize: number;
};

const DeviationAlarmRecord: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();

  const pagingInfo = useSearchParams<SearchParams>(50);
  const { searchParams, setTotalCount, finish, reset } = pagingInfo;
  const [loading, setLoading] = useState(true);

  const [tableData, setTableData] = useState<DeviationAlarmRecordItem[]>([]);
  const { loading: optionsLoading, optionsMap } = useDeviationRecordOptions();

  // 弹窗状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DeviationAlarmRecordItem>();

  // 批量删除相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条数据吗？删除后不可恢复。`,
      onOk: async () => {
        try {
          await apiV2DeviationRateAlarmRecordDeletePost({
            ids: selectedRowKeys.map(id => Number(id)),
          });
          message.success('删除成功');
          setSelectedRowKeys([]);
          fetch();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const fetch = () => {
    setLoading(true);

    // 解析报警对象值
    const alarmObjectValues = searchParams.alarmObjects || [];
    const energyMediumTopologyIds: number[] = [];
    const energyMediumTopologyNodeIds: number[] = [];

    alarmObjectValues.forEach(value => {
      const parsed = parseAlarmObjectValue(value);
      if (parsed) {
        energyMediumTopologyIds.push(parsed.energyMediumTopologyId);
        energyMediumTopologyNodeIds.push(parsed.topologyNodeId);
      }
    });

    // 构建请求参数
    const params: V2DeviationRateAlarmRecordPagePostRequest = {
      page: searchParams.pageOffset,
      size: searchParams.pageSize,
      energyMediumTopologyIds: energyMediumTopologyIds.length > 0 ? energyMediumTopologyIds : undefined,
      energyMediumTopologyNodeIds: energyMediumTopologyNodeIds.length > 0 ? energyMediumTopologyNodeIds : undefined,
      energyMediumIds: searchParams.energyMediumId ? [searchParams.energyMediumId] : undefined,
      deviationRateAlarmRuleIds: searchParams.deviationRateAlarmRuleIds,
      alarmLevels: searchParams.alarmLevels,
      alarmTimeFrom: searchParams.alarmTimeRange?.[0]?.startOf('day')?.valueOf(),
      alarmTimeTo: searchParams.alarmTimeRange?.[1]?.endOf('day')?.valueOf(),
      statCycleFromTime: searchParams.statTimeRange?.[0]?.startOf('day')?.valueOf(),
      statCycleToTime: searchParams.statTimeRange?.[1]?.endOf('day')?.valueOf(),
    };

    apiV2DeviationRateAlarmRecordPagePost(params)
      .then(res => {
        setTableData(res.list || []);
        setTotalCount(res.total || 0);
      })
      .finally(() => {
        !optionsLoading && setLoading(false);
      });
  };

  useEffect(() => {
    fetch();
  }, [searchParams]);

  useEffect(() => {
    if (!optionsLoading && loading) {
      setLoading(false);
    }
  }, [optionsLoading]);

  const handlerSearch = (values: FormValues) => {
    finish({ ...values, pageOffset: 1, pageSize: searchParams.pageSize });
  };

  const handlerReset = () => {
    reset();
  };

  // 报警详情
  const handleAlarmDetail = (record: DeviationAlarmRecordItem) => {
    navigate(`/deviation/alarm/record/detail/${record.id}`);
  };

  // 查看详情
  const handleViewDetail = (record: DeviationAlarmRecordItem) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const filters = (
    <CustomFilter form={form} onFinish={handlerSearch} onReset={handlerReset}>
      <Form.Item name="alarmObjects" label="报警对象">
        <Select placeholder="请选择" mode="multiple" options={optionsMap.alarmObjects} optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="energyMediumId" label="能源介质">
        <Select placeholder="请选择" options={optionsMap.energyMediumIds} optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="deviationRateAlarmRuleIds" label="报警规则">
        <Select
          placeholder="请选择"
          mode="multiple"
          options={optionsMap.deviationRateAlarmRuleIds}
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="alarmLevels" label="报警等级">
        <Select placeholder="请选择" mode="multiple" options={optionsMap.alarmLevels} optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="alarmTimeRange" label="报警时间">
        <DatePicker.RangePicker style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
      </Form.Item>
      <Form.Item name="statTimeRange" label="统计时间">
        <DatePicker.RangePicker style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
      </Form.Item>
    </CustomFilter>
  );

  const columns = useMemo(() => getTableListColumns(), []);
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 180,
      fixed: 'right' as const,
      key: 'action',
      render: (_: any, record: DeviationAlarmRecordItem) => (
        <Space size={8}>
          <Button type="link" size="small" onClick={() => handleAlarmDetail(record)}>
            报警详情
          </Button>
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            查看详情
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} isLoading={loading}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleBatchDelete}>
          批量删除
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={buildColumns}
        dataSource={tableData}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
      />
      <Paging pagingInfo={pagingInfo} />

      <DetailModal visible={detailModalVisible} record={selectedRecord} onClose={() => setDetailModalVisible(false)} />
    </Wrapper>
  );
};

export default DeviationAlarmRecord;
